import { omit } from "lodash";
import { Dispatch, SetStateAction, useState, useCallback } from "react";
import messages from "messages";
import { Form } from "react-bootstrap";
// utils
import getClasses from "utils/getClasses";
// components
import ActionsBar from "../../../../../actions-bar/ActionsBar";
import PlanFieldLabel from "../plan-field-label";
import ConfirmModal from "components/confirm-modal";
// styles
import styles from "./styles.module.scss";

type Props = {
  className?: string;
  readOnly?: boolean;
  value: AdvancedTreatmentPlan;
  setValue?: Dispatch<SetStateAction<AdvancedTreatmentPlan>>;
};

const KEY = "goals";

const GoalFields = ({ className, readOnly, value, setValue }: Props) => {
  const onCreate = useCallback(
    () =>
      setValue &&
      setValue((s) => ({
        ...s,
        [KEY]: !s[KEY]
          ? Array(2).fill({ value: "" })
          : [...(s[KEY] || []), { value: "" }],
      })),
    [setValue],
  );

  const onDelete = useCallback(
    (i: number) =>
      setValue &&
      setValue((s) => {
        if ((s?.[KEY] || []).length <= 1) {
          return omit(s as any, KEY) as typeof s;
        }
        return {
          ...s,
          [KEY]: [
            ...(s[KEY] || []).slice(0, i),
            ...(s[KEY] || []).slice(i + 1),
          ],
        };
      }),
    [setValue],
  );

  const onCreateObjective = useCallback(
    (goalIndex: number) =>
      setValue &&
      setValue((s) => {
        const goals = s[KEY] || [];
        const objectives = goals[goalIndex]?.objectives || [];
        return {
          ...s,
          [KEY]: [
            ...goals.slice(0, goalIndex),
            {
              ...goals[goalIndex],

              objectives: [...objectives, { value: "" }],
            },
            ...goals.slice(goalIndex + 1),
          ],
        };
      }),
    [setValue],
  );

  const onDeleteObjective = useCallback(
    (goalIndex: number, i: number) =>
      setValue &&
      setValue((s) => {
        const goals = s[KEY] || [];
        const objectives = goals[goalIndex]?.objectives || [];
        return {
          ...s,
          [KEY]: [
            ...goals.slice(0, goalIndex),
            {
              ...goals[goalIndex],

              objectives: [
                ...objectives.slice(0, i),
                ...objectives.slice(i + 1),
              ],
            },
            ...goals.slice(goalIndex + 1),
          ],
        };
      }),
    [setValue],
  );

  const onCreateIntervention = useCallback(
    (goalIndex: number, objectiveIndex: number) =>
      setValue &&
      setValue((s) => {
        const goals = s[KEY] || [];
        const objectives = goals[goalIndex]?.objectives || [];
        const interventions = objectives[objectiveIndex]?.interventions || [];
        return {
          ...s,
          [KEY]: [
            ...goals.slice(0, goalIndex),
            {
              ...goals[goalIndex],

              objectives: [
                ...objectives.slice(0, objectiveIndex),
                {
                  ...objectives[objectiveIndex],
                  interventions: [...interventions, ""],
                },
                ...objectives.slice(objectiveIndex + 1),
              ],
            },
            ...goals.slice(goalIndex + 1),
          ],
        };
      }),
    [setValue],
  );

  const onDeleteIntervention = useCallback(
    (goalIndex: number, objectiveIndex, i: number) =>
      setValue &&
      setValue((s) => {
        const goals = s[KEY] || [];
        const objectives = goals[goalIndex]?.objectives || [];
        const interventions = objectives[objectiveIndex]?.interventions || [];
        return {
          ...s,
          [KEY]: [
            ...goals.slice(0, goalIndex),
            {
              ...goals[goalIndex],

              objectives: [
                ...objectives.slice(0, objectiveIndex),
                {
                  value: objectives[objectiveIndex]?.value || "",
                  ...(interventions.length > 1
                    ? {
                        interventions: [
                          ...interventions.slice(0, i),
                          ...interventions.slice(i + 1),
                        ],
                      }
                    : {}),
                },
                ...objectives.slice(objectiveIndex + 1),
              ],
            },
            ...goals.slice(goalIndex + 1),
          ],
        };
      }),
    [setValue],
  );

  const [goalIndex, setGoalIndex] = useState(-1);

  return (
    <>
      <ConfirmModal
        show={goalIndex > -1}
        onCancel={() => setGoalIndex(-1)}
        onConfirm={() => {
          onDelete(goalIndex);
          setGoalIndex(-1);
        }}
        labelTitle={messages.deleteGoal}
        children={messages.ifYouRemoveGoal}
      />
      <div className={className}>
        {(value[KEY] || (readOnly ? [] : [{ value: "" }])).map(
          ({ value: goal, objectives }, goalIndex: number) => (
            <Form.Group
              key={goalIndex}
              className={styles["goal-fields__field"]}
            >
              <PlanFieldLabel
                required={goalIndex === 0}
                readOnly={readOnly}
                label={messages.goal + " " + (goalIndex + 1)}
              />
              <div
                key={goalIndex}
                className={styles["goal-fields__input-container"]}
              >
                {readOnly ? (
                  <div>{goal}</div>
                ) : (
                  <Form.Control
                    type={"text"}
                    value={goal}
                    onChange={(e) =>
                      setValue &&
                      setValue((s) => ({
                        ...s,
                        [KEY]: [
                          ...(s[KEY] || []).slice(0, goalIndex),
                          {
                            value: e.target.value,
                            objectives: s[KEY]?.[goalIndex]?.objectives || [
                              { value: "" },
                            ],
                          },
                          ...(s[KEY] || []).slice(goalIndex + 1),
                        ],
                      }))
                    }
                    placeholder={messages.enterYourDescription}
                    readOnly={readOnly}
                    plaintext={readOnly}
                  />
                )}
                {!readOnly && (
                  <ActionsBar
                    onDelete={
                      !!goal || (value[KEY] || []).length > 1 || goalIndex !== 0
                        ? () => setGoalIndex(goalIndex)
                        : undefined
                    }
                    onCreate={() => onCreate()}
                  />
                )}
              </div>

              {(objectives || []).map(
                ({ value: objective, interventions }, objectiveIndex) => (
                  <Form.Group
                    key={objectiveIndex}
                    className={styles["goal-fields__sub-field"]}
                  >
                    <PlanFieldLabel
                      required={goalIndex === 0 && objectiveIndex === 0}
                      subField={true}
                      readOnly={readOnly}
                      label={messages.objective + " " + (objectiveIndex + 1)}
                    />
                    <div
                      key={objectiveIndex}
                      className={styles["goal-fields__input-container"]}
                    >
                      {readOnly ? (
                        <div>{objective}</div>
                      ) : (
                        <Form.Control
                          type={"text"}
                          value={objective}
                          onChange={(e) =>
                            setValue &&
                            setValue((s) => {
                              const goals = s[KEY] || [];
                              const objectives =
                                goals[goalIndex]?.objectives || [];
                              return {
                                ...s,
                                [KEY]: [
                                  ...goals.slice(0, goalIndex),
                                  {
                                    ...goals[goalIndex],
                                    objectives: [
                                      ...objectives.slice(0, objectiveIndex),
                                      {
                                        ...objectives[objectiveIndex],
                                        value: e.target.value,
                                        interventions: objectives[
                                          objectiveIndex
                                        ]?.interventions?.length
                                          ? objectives[objectiveIndex]
                                              ?.interventions
                                          : [""],
                                      },
                                      ...objectives.slice(objectiveIndex + 1),
                                    ],
                                  },
                                  ...goals.slice(goalIndex + 1),
                                ],
                              };
                            })
                          }
                          placeholder={messages.enterYourDescription}
                          readOnly={readOnly}
                          plaintext={readOnly}
                        />
                      )}
                      {!readOnly && (
                        <ActionsBar
                          onDelete={
                            (value[KEY]?.[goalIndex].objectives || []).length >
                              1 || objectiveIndex !== 0
                              ? () =>
                                  onDeleteObjective(goalIndex, objectiveIndex)
                              : undefined
                          }
                          onCreate={() => onCreateObjective(goalIndex)}
                        />
                      )}
                    </div>
                    {interventions &&
                      interventions.map((intervention, interventionIndex) => (
                        <Form.Group
                          key={interventionIndex}
                          className={getClasses(
                            styles["goal-fields__sub-field"],
                            styles["goal-fields__sub-field--intervention"],
                          )}
                        >
                          <PlanFieldLabel
                            subField={true}
                            readOnly={readOnly}
                            label={
                              messages.intervention +
                              " " +
                              (interventionIndex + 1)
                            }
                          />
                          <div
                            key={interventionIndex}
                            className={styles["goal-fields__input-container"]}
                          >
                            {readOnly ? (
                              <div>{intervention}</div>
                            ) : (
                              <Form.Control
                                type={"text"}
                                value={intervention}
                                onChange={(e) =>
                                  setValue &&
                                  setValue((s) => {
                                    const goals = s[KEY] || [];
                                    const objectives =
                                      goals[goalIndex]?.objectives || [];
                                    const interventions =
                                      objectives[objectiveIndex]
                                        ?.interventions || [];

                                    return {
                                      ...s,
                                      [KEY]: [
                                        ...goals.slice(0, goalIndex),
                                        {
                                          ...goals[goalIndex],
                                          objectives: [
                                            ...objectives.slice(
                                              0,
                                              objectiveIndex,
                                            ),
                                            {
                                              ...objectives[objectiveIndex],
                                              interventions: [
                                                ...interventions.slice(
                                                  0,
                                                  interventionIndex,
                                                ),
                                                e.target.value,
                                                ...interventions.slice(
                                                  interventionIndex + 1,
                                                ),
                                              ],
                                            },
                                            ...objectives.slice(
                                              objectiveIndex + 1,
                                            ),
                                          ],
                                        },
                                        ...goals.slice(goalIndex + 1),
                                      ],
                                    };
                                  })
                                }
                                placeholder={messages.enterYourDescription}
                                readOnly={readOnly}
                                plaintext={readOnly}
                              />
                            )}
                            {!readOnly && (
                              <ActionsBar
                                onDelete={() =>
                                  onDeleteIntervention(
                                    goalIndex,
                                    objectiveIndex,
                                    interventionIndex,
                                  )
                                }
                                onCreate={() =>
                                  onCreateIntervention(
                                    goalIndex,
                                    objectiveIndex,
                                  )
                                }
                              />
                            )}
                          </div>
                        </Form.Group>
                      ))}
                  </Form.Group>
                ),
              )}
            </Form.Group>
          ),
        )}
      </div>
    </>
  );
};

export default GoalFields;
